import { TableRow, TableCell, Typography } from '@mui/material';
import { AnyPlan, DigitalEntry, DigitalRow } from '../../../types';
import { TouchpointGroup } from '../types';
import { hasAnyDifference, isDifferentFromBase } from '../helpers';
import DigitalFunnelSection from '../DigitalFunnelSection/DigitalFunnelSection';

type DigitalTouchpointSectionProps = {
  country: string;
  plans: AnyPlan[];
  formatBudget: (v?: number, currency?: string) => string;
  showOnlyDifferences: boolean;
  group: TouchpointGroup;
  getDigitalEntry: (
    plan: AnyPlan,
    country: string,
    row: DigitalRow
  ) => DigitalEntry | undefined;
  getPriorityLabel: (entry?: DigitalEntry) => string;
};

function aggregateDigitalEntriesForPlan(
  rows: DigitalRow[],
  plan: AnyPlan,
  country: string,
  getDigitalEntry: (
    plan: AnyPlan,
    country: string,
    row: DigitalRow
  ) => DigitalEntry | undefined
): DigitalEntry | undefined {
  const entries = rows
    .map((row) => getDigitalEntry(plan, country, row))
    .filter((e): e is DigitalEntry => !!e);

  if (!entries.length) return undefined;

  const aggregated: any = { ...entries[0] };

  const sumField = (field: string) =>
    entries.reduce((acc, e: any) => acc + (e?.[field] ?? 0), 0);

  const impressions = sumField('impressions');
  const reach = sumField('reach');
  const clicks = sumField('clicks');
  const videoViews = sumField('videoViews');

  aggregated.impressions = impressions || undefined;
  aggregated.reach = reach || undefined;
  aggregated.clicks = clicks || undefined;
  aggregated.videoViews = videoViews || undefined;

  const budgets = entries
    .map((e: any) => e.budget)
    .filter((b: any) => b && typeof b.value === 'number');

  if (budgets.length) {
    aggregated.budget = {
      value: budgets.reduce((acc: number, b: any) => acc + b.value, 0),
      currency: budgets[0].currency,
    };
  } else {
    aggregated.budget = undefined;
  }

  return aggregated as DigitalEntry;
}

export default function DigitalTouchpointSection({
  country,
  plans,
  formatBudget,
  showOnlyDifferences,
  group,
  getDigitalEntry,
  getPriorityLabel,
}: DigitalTouchpointSectionProps) {
  const tpAggregates = plans.map((plan) =>
    aggregateDigitalEntriesForPlan(group.rows, plan, country, getDigitalEntry)
  );
  const tpBudgetValues = tpAggregates.map((agg) => agg?.budget?.value);

  return (
    <>
      {/* Touchpoint total */}
      {(!showOnlyDifferences ||
        hasAnyDifference<number | undefined>(tpBudgetValues)) && (
        <TableRow
          sx={{
            height: '60px',
            background: '#F5F5F5',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell sx={{ border: '1px solid', borderColor: '#C7D8F9' }}>
            <Typography fontWeight={600} fontSize="14px">
              {group.touchpointName}
            </Typography>
          </TableCell>
          {plans.map((plan, index) => {
            const agg = tpAggregates[index];
            const diff = isDifferentFromBase<number | undefined>(
              tpBudgetValues,
              index
            );

            return (
              <TableCell
                key={`${group.touchpointName}-tp-budget-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 700,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {agg?.budget
                  ? formatBudget(agg.budget.value, agg.budget.currency)
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Funnels within this touchpoint */}
      {group.funnels.map((funnel) => {
        const funnelAggs = plans.map((plan) =>
          aggregateDigitalEntriesForPlan(
            funnel.rows,
            plan,
            country,
            getDigitalEntry
          )
        );

        return (
          <DigitalFunnelSection
            key={`${group.touchpointName}-${funnel.funnelName}`}
            touchpointName={group.touchpointName}
            funnelName={funnel.funnelName}
            plans={plans}
            formatBudget={formatBudget}
            showOnlyDifferences={showOnlyDifferences}
            funnelAggs={funnelAggs}
            getPriorityLabel={getPriorityLabel}
          />
        );
      })}
    </>
  );
}
